import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArrowLeft, ArrowRight, Loader2, CalendarDays } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (u) => (u && u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
};

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="App min-h-screen bg-white" data-testid="article-page">
      <Header />
      {loading ? (
        <div className="h-96 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
        </div>
      ) : notFound || !article ? (
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Article introuvable</h1>
          <Link to="/actualites" className="mt-6 inline-flex items-center gap-2 text-[#2563EB] font-semibold">
            <ArrowLeft className="w-4 h-4" /> Retour aux actualités
          </Link>
        </div>
      ) : (
        <>
          <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
            <Link to="/actualites" data-testid="article-back" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" /> Actualités &amp; Conseils
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-blue-50 text-[#2563EB]">
                {article.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <CalendarDays className="w-3 h-3" /> {fmtDate(article.created_at)}
              </span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900 leading-tight" data-testid="article-title">
              {article.title}
            </h1>
            {article.excerpt && <p className="mt-5 text-lg text-slate-500 leading-relaxed">{article.excerpt}</p>}

            {article.cover_image && (
              <img
                src={mediaSrc(article.cover_image)}
                alt={article.title}
                className="w-full rounded-3xl mt-8 object-cover max-h-[440px]"
                data-testid="article-cover"
              />
            )}

            <div
              className="article-content mt-10"
              data-testid="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-20">
            <div className="bg-slate-900 rounded-3xl p-8 sm:p-10 text-center">
              <h3 className="font-display text-2xl font-extrabold text-white">Curieux de connaître la valeur de votre bien ?</h3>
              <p className="mt-2 text-slate-300">Obtenez un avis de valeur gratuit et instantané en moins de 2 minutes.</p>
              <Link
                to="/estimation"
                data-testid="article-cta"
                className="mt-6 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Estimer mon bien <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </>
      )}
      <Footer />
    </div>
  );
}
